import {FC, PropsWithChildren, useCallback, useMemo, useRef, useState} from "react";
import {
    DialogInput,
    FeedbackContext,
    FeedbackContextType,
    isDialogInput,
} from "../context/Feedback";
import BaseSnackbar, {SnackbarCloseReason, SnackbarProps as BaseSnackbarProps} from "@mui/material/Snackbar";
import Slide, {SlideProps} from '@mui/material/Slide';
import Alert from "@mui/material/Alert";
import FeedbackDialog from "@memaro/components/FeedbackDialog.tsx";

type FeedbackProviderProps = PropsWithChildren

type Severity = 'success' | 'warning' | 'error'

type FeedbackRecord = {
    id: number
    severity: Severity
    message: string
}

type DialogState = DialogInput & {
    type: "confirmation" | "info"
    open: boolean
    resolve?: (result: boolean) => void
}

const FeedbackProvider: FC<FeedbackProviderProps> = ({children}) => {
    const [records, setRecords] = useState<FeedbackRecord[]>([])
    const [dialog, setDialog] = useState<DialogState | undefined>(undefined)

    const counter = useRef<number>(0)

    const handleFeedback = useCallback((msg: string, severity: Severity) => {
        setRecords(prev => [...prev, {
            id: counter.current,
            severity: severity,
            message: msg,
        }])
        counter.current++
    }, [])

    const handleSuccess = useCallback((msg: string) => handleFeedback(msg, 'success'), [handleFeedback])
    const handleWarning = useCallback((msg: string) => handleFeedback(msg, 'warning'), [handleFeedback])
    const handleError = useCallback((msg: string) => handleFeedback(msg, 'error'), [handleFeedback])
    const handleClose = useCallback((id: number) => {
        setRecords(prev => prev.filter(rec => rec.id != id))
    }, [])

    const handleConfirm = useCallback((input: DialogInput | DialogInput["contents"]) => {
        return new Promise<boolean>((resolve) => {
            if (isDialogInput(input)) {
                setDialog({...input, resolve, open: true, type: "confirmation"})
            } else {
                setDialog({contents: input, resolve, open: true, type: "confirmation"})
            }
        });
    }, [])

    const closeDialog = useCallback(() => {
        setDialog(prev => prev ? {
            ...prev,
            open: false,
        } : undefined)
        setTimeout(() => setDialog(undefined), 300) // wait for dialog to close before clearing
    }, [])

    const handleConfirmation = useCallback(() => {
        dialog?.resolve?.(true)
        closeDialog()
    }, [closeDialog, dialog])

    const handleCloseDialog = useCallback(() => {
        dialog?.resolve?.(false)
        closeDialog()
    }, [closeDialog, dialog])

    const handleInfo = useCallback((input: DialogInput | DialogInput["contents"]) => {
        if (isDialogInput(input)) {
            setDialog({...input, open:true,  type: "info"})
        } else {
            setDialog({contents: input, open:true, type: "info"})
        }
    }, [])


    const value = useMemo((): FeedbackContextType => ({
        success: handleSuccess,
        warning: handleWarning,
        error: handleError,
        confirm: handleConfirm,
        info: handleInfo,
    }), [handleConfirm, handleError, handleInfo, handleSuccess, handleWarning])

    return (
        <FeedbackContext.Provider value={value}>
            {records.map(rec => (
                <Snackbar key={rec.id} {...rec} onClose={handleClose}/>
            ))}
            <FeedbackDialog
                id="feedback"
                open={Boolean(dialog?.open)}
                onConfirm={dialog?.type == "confirmation" ? handleConfirmation : undefined}
                onClose={handleCloseDialog}
                title={dialog?.title}
                contents={dialog?.contents ?? ""}
            />
            {children}
        </FeedbackContext.Provider>
    )
}

type SnackbarProps = FeedbackRecord & {
    onClose: (id: number) => void
}

const snackbarDefaultProps: Pick<BaseSnackbarProps, 'slots' | 'anchorOrigin'> = {
    slots: {
        transition: (props: SlideProps) => (
            <Slide {...props} timeout={300} direction="down"/>
        )
    },
    anchorOrigin: {
        vertical: "top",
        horizontal: "center"
    }
}

const Snackbar: FC<SnackbarProps> = ({id, severity, message, onClose}) => {
    const [open, setOpen] = useState<boolean>(true)
    const handleClose = useCallback((_: unknown, reason?: SnackbarCloseReason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false)
        setTimeout(() => onClose(id), 300)
    }, [id, onClose])

    return (
        <BaseSnackbar
            autoHideDuration={5000}
            open={open}
            onClose={handleClose}
            {...snackbarDefaultProps}
        >
            <Alert
                onClose={handleClose}
                severity={severity}
                variant="filled"
                sx={{width: '100%'}}
            >
                {message}
            </Alert>
        </BaseSnackbar>
    )
}

export default FeedbackProvider
